import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import { useStudentAcademicQuery } from 'core/graphql';

import { toStudentGlobalId } from './useStudentDetail';

/**
 * Academic data shape the `AcademicPanel` consumes (TZ-1).
 */
export interface IStudentAcademicLesson {
  id: string;
  date: string;
  lessonNumber?: number | null;
  name?: string | null;
  attended?: boolean | null;
  mark?: number | null;
}
export interface IStudentAcademicData {
  attendancePercent?: number | null;
  averageMark?: number | null;
  lessons: IStudentAcademicLesson[];
}

/**
 * Result of the academic hook. Kept as a discriminated union so the panel can
 * stay generic; `available` is always `true` now that the backend ships the
 * academic fields (TZ-1 confirmed). The `available: false` arm is retained for
 * the `skip` (no id) case and for forward compatibility.
 */
export type IUseStudentAcademic =
  | { available: false }
  | {
      available: true;
      loading: boolean;
      error?: ApolloError;
      data?: IStudentAcademicData;
      refetch: () => void;
    };

/**
 * Academic hook — backs the `AcademicPanel` (TZ-1).
 *
 * Calls the dedicated lazy `StudentAcademic` query (the heavy `lessons`
 * connection loads only when the panel renders). `errorPolicy: 'all'` so a
 * partial sub-field error doesn't blank the panel, and `attended: null`
 * (not-yet-marked) is preserved distinct from `false` (absent).
 *
 * @param studentId student id (bare PK or Relay global id); without it the hook
 *   stays gated off (nothing to fetch).
 */
export const useStudentAcademic = (studentId?: string): IUseStudentAcademic => {
  const { data, loading, error, refetch } = useStudentAcademicQuery({
    variables: {
      id: studentId ? toStudentGlobalId(studentId) : '',
      first: 100,
    },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const mapped = useMemo<IStudentAcademicData | undefined>(() => {
    const student = data?.student;
    if (!student) return undefined;
    return {
      attendancePercent: student.attendancePercent,
      averageMark: student.averageMark,
      lessons: (student.lessons?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({
          id: node.id,
          date: node.date,
          lessonNumber: node.lessonNumber,
          name: node.name,
          attended: node.attended,
          mark: node.mark,
        })),
    };
  }, [data]);

  if (!studentId) {
    return { available: false };
  }

  return {
    available: true,
    loading,
    error,
    data: mapped,
    refetch: () => {
      refetch();
    },
  };
};
